import { compareOccurrencesByPage } from "../../../utils/compareOccurrencesByPage";
import { removeBoardItemGroupFromDiagram } from "../../../utils/removeBoardItemGroupFromDiagram";
import { setBoardItemLvMatchInDiagram } from "../../../utils/setBoardItemLvMatchInDiagram";
import type {
  BoardDiagramV3,
  BoardItemOccurrenceV3,
  BoardItemTypeV3,
  LvMatch,
} from "../../../utils/types";
import type {
  DiagramItemOccurrenceSnapshot,
  DiagramItemTypeChange,
  DiagramItemTypeSnapshot,
} from "../../types";
import { DIAGRAM_LV_MATCH_COLUMNS } from "./constants";
import type { ApplyHistoryChangesToDiagram } from "./types";

const lvMatchOf = (type: DiagramItemTypeSnapshot): LvMatch => ({
  lvId: type.lvMatchId ?? undefined,
  lvName: type.lvMatchName ?? undefined,
  confidence: type.lvMatchConfidence ?? undefined,
  reasoning: type.lvMatchReasoning ?? undefined,
});

const isShown = (diagram: BoardDiagramV3, typeId: number): boolean =>
  diagram.itemTypes.some(
    (itemType) => itemType.diagram_item_type_id === typeId,
  );

const toItemType = (
  diagram: BoardDiagramV3,
  rowId: number,
  type: DiagramItemTypeSnapshot,
  occurrences: { rowId: number; occurrence: DiagramItemOccurrenceSnapshot }[],
): BoardItemTypeV3 | null => {
  const pageById = new Map(diagram.pages.map((page) => [page.id, page]));
  const boardOccurrences: BoardItemOccurrenceV3[] = [];
  for (const { rowId: occurrenceId, occurrence } of occurrences) {
    const page = pageById.get(occurrence.pageId);
    // A page the cached diagram does not list; the caller reloads.
    if (page === undefined) return null;
    boardOccurrences.push({
      occurrence_id: occurrenceId,
      page: page.page,
      imageUrl: page.imageUrl,
      pdfUrl: page.pdfUrl,
      widthPx: page.widthPx,
      heightPx: page.heightPx,
      imageScaleFactor: page.imageScaleFactor,
      count: occurrence.count,
      circuits: occurrence.breakerIds,
    });
  }
  boardOccurrences.sort(compareOccurrencesByPage);
  return {
    diagram_item_type_id: rowId,
    item: type.item,
    text: type.text,
    unit: type.unit,
    // The load leaves the LV match out when no position is set.
    lvMatch: type.lvMatchId === null ? undefined : lvMatchOf(type),
    totalCount: boardOccurrences.reduce((sum, item) => sum + item.count, 0),
    occurrences: boardOccurrences,
  };
};

export const applyHistoryChangesToDiagram: ApplyHistoryChangesToDiagram = ({
  diagram,
  changes,
}) => {
  const typeChanges: DiagramItemTypeChange[] = [];
  const createdOccurrencesByTypeId = new Map<
    number,
    { rowId: number; occurrence: DiagramItemOccurrenceSnapshot }[]
  >();
  for (const change of changes) {
    if (change.table === "diagramItemTypes") typeChanges.push(change);
    if (
      change.table === "diagramItemOccurrences" &&
      change.changeType === "created" &&
      change.after !== null
    ) {
      const { itemTypeId } = change.after;
      const created = createdOccurrencesByTypeId.get(itemTypeId) ?? [];
      created.push({ rowId: change.rowId, occurrence: change.after });
      createdOccurrencesByTypeId.set(itemTypeId, created);
    }
  }

  let next = diagram;
  for (const { changeType, rowId } of typeChanges) {
    if (changeType !== "deleted") continue;
    if (!isShown(next, rowId)) return { kind: "reload" };
    next = removeBoardItemGroupFromDiagram({
      diagram: next,
      diagramItemTypeId: rowId,
    });
  }

  for (const { changeType, rowId, after } of typeChanges) {
    if (changeType !== "created" || after === null) continue;
    // The load merges types by item and text, so a twin already shown means the cache cannot hold this one.
    const isTwinShown = next.itemTypes.some(
      (itemType) =>
        itemType.diagram_item_type_id === rowId ||
        (itemType.item === after.item && (itemType.text ?? "") === after.text),
    );
    if (isTwinShown) return { kind: "reload" };
    const restored = toItemType(
      next,
      rowId,
      after,
      createdOccurrencesByTypeId.get(rowId) ?? [],
    );
    if (restored === null) return { kind: "reload" };
    next = {
      ...next,
      itemTypes: [...next.itemTypes, restored],
      totalCount: next.totalCount + restored.totalCount,
    };
  }

  for (const { changeType, rowId, before, after } of typeChanges) {
    if (changeType !== "updated" || before === null || after === null) continue;
    if (!isShown(next, rowId)) return { kind: "reload" };
    const isLvMatchChanged = DIAGRAM_LV_MATCH_COLUMNS.some(
      (column) => before[column] !== after[column],
    );
    if (!isLvMatchChanged) continue;
    next = setBoardItemLvMatchInDiagram({
      diagram: next,
      diagramItemTypeId: rowId,
      lvMatch: lvMatchOf(after),
    });
  }

  return { kind: "patched", diagram: next };
};
